import { useCallback, useMemo } from 'react';
import { t } from '@lingui/macro';

import { Favorite, Search } from '@rover/icons';
import usePageContext from '@rover/react-lib/src/context/usePageContext';
import MobileSearchTrainersCategories from '@rover/react-lib/src/pages/training/constants/mobileSearchTrainersCategories';
import TrainingUrls from '@rover/react-lib/src/pages/training/constants/urls';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';
import useIsBrightHorizonsNavigation from '@rover/shared/js/hooks/useIsBrightHorizonsNavigation';

import { getShouldShowItem, MenuItemType, MenuItemVisibility } from '../utilities';

const useMobileMenuItems = (): MenuItemType[] => {
  const { isActiveServiceProvider = false, isAuthenticated = false, user } = useCurrentUserShim();
  const isBrightHorizonsNavigation = useIsBrightHorizonsNavigation();
  const { pageCategory } = usePageContext();
  // we memoize searchTrainersShouldShow to avoid changing getMenuItems without need
  const shouldShowSearchTrainers = useMemo(
    () =>
      Object.values(MobileSearchTrainersCategories).includes(
        pageCategory as unknown as MobileSearchTrainersCategories
      ),
    [pageCategory]
  );

  const getMenuItems = useCallback(
    (): MenuItemType[] =>
      [
        {
          path: '/search/',
          displayName: t`Search Sitters`,
          visibility: [
            MenuItemVisibility.SITTER,
            MenuItemVisibility.OWNER,
            MenuItemVisibility.UNAUTHENTICATED,
          ],
          icon: Search,
          isBHCompatible: false,
          shouldShowItem: !shouldShowSearchTrainers,
        },
        {
          path: TrainingUrls.SEARCH,
          displayName: 'Search Trainers',
          visibility: [
            MenuItemVisibility.SITTER,
            MenuItemVisibility.OWNER,
            MenuItemVisibility.UNAUTHENTICATED,
          ],
          icon: Search,
          isBHCompatible: false,
          shouldShowItem: shouldShowSearchTrainers,
        },
        {
          path: '/provider-profile/',
          displayName: t`List More Services`,
          visibility: [MenuItemVisibility.SITTER],
          icon: Favorite,
          isBHCompatible: false,
        },
        {
          path: '/become-a-sitter/',
          displayName: t`Become a Sitter`,
          visibility: [MenuItemVisibility.OWNER, MenuItemVisibility.UNAUTHENTICATED],
          icon: Favorite,
          isBHCompatible: false,
        },
        {
          path: '/bright-horizons/',
          displayName: t`My Back-Up Care credits`,
          visibility: [
            MenuItemVisibility.SITTER,
            MenuItemVisibility.OWNER,
            MenuItemVisibility.BRIGHT_HORIZONS,
          ],
          isBHCompatible: true,
        },
        {
          path: '/account/inbox/',
          displayName: t`Inbox`,
          visibility: [MenuItemVisibility.SITTER, MenuItemVisibility.OWNER],
          isBHCompatible: true,
        },
        {
          path: '/account/',
          displayName: t`Dashboard`,
          visibility: [MenuItemVisibility.SITTER, MenuItemVisibility.OWNER],
          isBHCompatible: true,
        },
        {
          path: '/provider-profile/',
          displayName: t`Profile`,
          visibility: [MenuItemVisibility.SITTER],
          isBHCompatible: true,
        },
        {
          path: '/account/calendar/',
          displayName: t`Calendar`,
          visibility: [MenuItemVisibility.SITTER],
          isBHCompatible: true,
        },
        {
          path: '/provider-profile/photos/',
          displayName: t`Photos`,
          visibility: [MenuItemVisibility.SITTER],
          isBHCompatible: true,
        },
        ...(user?.slug
          ? [
              {
                path: `/members/${user.slug}/dogs/`,
                displayName: t`Your Pets`,
                visibility: [MenuItemVisibility.OWNER],
                isBHCompatible: true,
              },
            ]
          : []),
        {
          path: '/account/profile/',
          displayName: t`Profile`,
          visibility: [MenuItemVisibility.OWNER],
          isBHCompatible: true,
        },
        {
          path: '/account/profile/settings/',
          displayName: t`Settings`,
          visibility: [MenuItemVisibility.SITTER, MenuItemVisibility.OWNER],
          isBHCompatible: true,
        },
      ].filter((menuItem) =>
        getShouldShowItem(
          menuItem.visibility,
          isAuthenticated,
          isActiveServiceProvider,
          isBrightHorizonsNavigation,
          menuItem.isBHCompatible,
          menuItem.shouldShowItem
        )
      ),
    [
      user,
      isAuthenticated,
      isActiveServiceProvider,
      isBrightHorizonsNavigation,
      shouldShowSearchTrainers,
    ]
  );

  return getMenuItems();
};

export default useMobileMenuItems;
